import { TargetBasedVestingMilestoneItem } from '../types/vesting-tasks.types';
import { toNumber } from './to-number.util';

const clampWeight = (weight: number) => Math.max(Math.min(toNumber(weight), 1), 0);

const vestedFromWeights = (totalSharesCount: number, weights: number[]) =>
  weights.reduce((acc, weight) => acc + Math.floor(totalSharesCount * weight), 0);

export type CalcMilestoneIssuedSharesProps = {
  milestoneIndex: number;
  milestones: Pick<TargetBasedVestingMilestoneItem, 'isCompleted' | 'weight'>[];
  totalSharesCount: number;
};

// Mirrors TargetBasedCalcSharesFromVestingStrategy.calculateSharesToVestNow on the backend so the
// approval tables show the amount that will actually vest. Two details are easy to get wrong and
// both produced visibly wrong "Issued Shares" in the approval modals:
//   - only milestones the backend has already marked `isCompleted` count as vested. Treating every
//     lower-indexed milestone as vested understates the remainder whenever milestones are approved
//     out of order or skipped.
//   - the base quantity is the vesting task's `totalSharesCount`, not the grant's `numbersOfGrants`.
//     They diverge once shares are returned to the pool (termination).
export const calcMilestoneIssuedShares = ({
  milestoneIndex,
  milestones,
  totalSharesCount,
}: CalcMilestoneIssuedSharesProps): number => {
  const total = Math.max(toNumber(totalSharesCount), 0);
  const milestone = milestones[milestoneIndex];

  if (!milestone) return 0;

  // The milestone being approved is pending by definition, even when the caller hands us a row the
  // backend already flipped to completed.
  const completedWeights = milestones
    .filter((item, index) => index !== milestoneIndex && item.isCompleted)
    .map(({ weight }) => clampWeight(weight));

  const previouslyVested = vestedFromWeights(total, completedWeights);
  const isLastMilestone = milestoneIndex === milestones.length - 1;

  // The final milestone absorbs the per-milestone rounding remainder, but only once something
  // vested before it — otherwise it vests just its own weight.
  if (isLastMilestone && previouslyVested > 0) return Math.max(total - previouslyVested, 0);

  const vestedAfterThisMilestone = vestedFromWeights(total, [
    ...completedWeights,
    clampWeight(milestone.weight),
  ]);

  return Math.max(vestedAfterThisMilestone - previouslyVested, 0);
};
